import React, { useState, useEffect, useRef } from 'react';
import { formatStepError } from './stepError';
import type { DoctorProfile } from '../../../types/doctorOnboarding';
import { uploadFile } from '../../../services/doctor-onboarding/doctorOnboardingService';

type Props = {
  profile: DoctorProfile | null;
  onSave: (bio: string, profilePhotoUrl?: string) => Promise<void>;
  onNext: () => void;
  onBack: () => void;
};

export default function BioStep({ profile, onSave, onNext, onBack }: Props) {
  const [bio, setBio] = useState(profile?.bio ?? '');
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoInputKey, setPhotoInputKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setBio(profile?.bio ?? '');
  }, [profile]);

  // Preview URL for selected file (object URL); revoke on change/unmount to avoid leaks
  const [previewObjectUrl, setPreviewObjectUrl] = useState<string | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  useEffect(() => {
    if (!photo) {
      setPreviewObjectUrl(null);
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      return;
    }
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    const url = URL.createObjectURL(photo);
    previewUrlRef.current = url;
    setPreviewObjectUrl(url);
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, [photo]);

  // Show selected file preview or existing profile photo (so it shows when user goes back)
  const displayPhotoUrl = previewObjectUrl ?? profile?.profile_photo_url ?? null;
  const hasPhoto = !!photo || !!(profile?.profile_photo_url);

  // Both bio and profile photo are required
  const isBioValid = bio.trim().length > 0;
  const isValid = isBioValid && hasPhoto;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setLoading(true);
    setError(null);
    try {
      let url: string | undefined;
      if (photo) {
        const upload = await uploadFile(photo, 'profile');
        url = upload.url;
      } else if (profile?.profile_photo_url) {
        url = profile.profile_photo_url;
      }
      await onSave(bio.trim(), url!);
      setPhoto(null);
      setPhotoInputKey((prev) => prev + 1);
      onNext();
    } catch (e: any) {
      setError(formatStepError(e, 'Failed to save bio'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="onboarding-section">
        <h3>Bio & Profile Photo</h3>
        <form id="bio-form" onSubmit={handleSave}>
          <div className="form-group bio-photo-section">
            <div className="bio-photo-preview-wrap">
              {displayPhotoUrl ? (
                <img
                  src={displayPhotoUrl}
                  alt="Profile"
                  className="bio-photo-preview"
                />
              ) : (
                <div className="bio-photo-placeholder" aria-hidden>
                  <span className="bio-photo-placeholder-icon">👤</span>
                  <span className="bio-photo-placeholder-text">Photo required</span>
                </div>
              )}
              {!hasPhoto && (
                <span className="field-hint">Profile photo is required.</span>
              )}
              <div className="bio-photo-actions">
                <label className="btn-secondary bio-photo-label">
                  <input
                    key={photoInputKey}
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                    className="bio-photo-input"
                  />
                  {hasPhoto ? 'Change photo' : 'Choose file'}
                </label>
              </div>
            </div>
          </div>
          <div className="form-group">
            <label>Professional Bio (max 500 chars) <span className="required">*</span></label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value.slice(0, 500))}
              rows={6}
              aria-invalid={!isBioValid && bio.length > 0}
            />
            {!isBioValid && (
              <span className="field-hint">Bio is required.</span>
            )}
          </div>
          {error && <div className="onboarding-error">{error}</div>}
        </form>
      </div>

      <div className="step-actions">
        <button type="button" className="btn-secondary" onClick={onBack}>
          Back
        </button>
        <button type="submit" form="bio-form" className="btn-primary" disabled={loading || !isValid}>
          {loading ? 'Saving...' : 'Save & Continue'}
        </button>
      </div>
    </div>
  );
}
